import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import isNil from 'lodash-es/isNil';
import trim from 'lodash-es/trim';
import { SubscriptionComponent } from '@liebherr/old-sage-ui';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { AppState } from '../../../app.models';
import * as fromFirmwares from '../../../firmwares';

@Component({
    selector: 'liebherr-firmware-search',
    templateUrl: 'firmware-search.component.html',
    styleUrls: ['firmware-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FirmwareSearchComponent extends SubscriptionComponent implements AfterViewInit, OnChanges {
    @Input() parentControl?: UntypedFormControl;
    @Input() routerType?: string;

    fwColumns = ['version'];
    fwFilterCtrl = this.fb.control(undefined);

    fwFilter$ = new BehaviorSubject<string | undefined>(undefined);
    page$ = new BehaviorSubject<number>(0);
    availableFw$: Observable<fromFirmwares.Firmware[]>;
    loading$ = this.store.select(fromFirmwares.selectLoading);

    dataSource = new MatTableDataSource<fromFirmwares.Firmware>([]);

    private routerType$ = new BehaviorSubject<string | undefined>(undefined);

    @ViewChild(MatPaginator) paginator?: MatPaginator;
    constructor(private store: Store<AppState>, private fb: UntypedFormBuilder) {
        super();

        this.availableFw$ = this.store.select(fromFirmwares.selectFirmwares).pipe(
            map((fw) => {
                return fw?.filter((f) => !f.activeOn && f.routerType === this.routerType);
            })
        );
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.routerType) {
            this.routerType$.next(this.routerType);
        }
    }

    ngAfterViewInit() {
        if (this.paginator) {
            this.dataSource.paginator = this.paginator;
        }

        this.sub.add(
            this.availableFw$.subscribe((firmwares) => {
                this.dataSource.data = firmwares;
            })
        );
        // update fw filter string after change of value of fwFilterCtrl
        this.sub.add(
            this.fwFilterCtrl.valueChanges.pipe(distinctUntilChanged(isEqual), debounceTime(500)).subscribe((change) => {
                const f = !isEmpty(trim(change)) ? trim(change) : undefined;
                this.fwFilter$.next(f);
            })
        );
        // load available fws on filter or page change
        this.sub.add(
            combineLatest([this.fwFilter$, this.page$]).subscribe(([filter, page]) => {
                this.store.dispatch(fromFirmwares.loadFirmwares());
            })
        );
        // update validity and update parentControl on change of filter or available fws
        this.sub.add(
            this.fwFilter$.pipe(distinctUntilChanged(), withLatestFrom(this.availableFw$)).subscribe(([filter, firmwares]) => {
                let errors: any = null;
                // check value
                if (isNil(filter)) {
                    errors = { ...errors, required: true };
                }
                // check if firmware exists
                const match = firmwares.find((fw) => fw.version === filter);
                if (isNil(match) && !isNil(filter)) {
                    errors = { ...errors, firmware: true };
                }
                // set errors of control
                this.fwFilterCtrl.setErrors(errors);

                if (!isEmpty(errors)) {
                    this.parentControl?.patchValue(null);
                    this.parentControl?.updateValueAndValidity();
                }

                // update parent control when valid
                if (this.fwFilterCtrl.valid && this.fwFilterCtrl.dirty) {
                    this.parentControl?.patchValue(match?.id);
                    this.parentControl?.markAsDirty();
                }
            })
        );
    }

    onFocus(trigger?: MatMenuTrigger) {
        if (trigger) {
            trigger.openMenu();
        }
    }

    onPage(event: PageEvent) {
        this.page$.next(event.pageIndex);
    }

    onSelection(fw: fromFirmwares.Firmware, trigger?: MatMenuTrigger) {
        this.fwFilterCtrl.patchValue(fw.version);
        this.fwFilterCtrl.markAsDirty();
        if (trigger) {
            trigger.closeMenu();
        }
    }
}
